// ─── Herramientas de trabajo del helpdesk ─────────────────────────────────────
// Etiquetas, notas privadas con menciones, macros, reglas de automatización y
// copiloto IA (microservicio conversations). Mismo patrón de headers que
// helpdeskFoldersService: accessToken vía authFetch + x-app/application.
//
// Los errores se lanzan con `error.status` poblado: 409 es semántico (nombre
// duplicado, tope alcanzado, IA no disponible) y la UI lo muestra inline.

import { authFetch } from "./authFetch";

// REACT_APP_API_CONNECTION ya incluye /api/v1/ al final; se recorta para poder
// armar paths completos sin duplicar el prefijo.
const BFF_BASE_URL = (process.env.REACT_APP_API_CONNECTION || "").replace(
  /\/api\/v1\/?$/,
  ""
);

async function request(path, options = {}) {
  const response = await authFetch(`${BFF_BASE_URL}${path}`, {
    ...options,
    headers: {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      "x-app": "web",
      application: "web",
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(
      data?.message || data?.detail || `Error ${response.status}`
    );
    error.status = response.status;
    throw error;
  }
  return data;
}

const json = (method, body) => ({ method, body: JSON.stringify(body) });

// ── Etiquetas ────────────────────────────────────────────────────────────────

/**
 * Etiquetas del tenant con `count` de conversaciones no resueltas.
 * @returns {Promise<Array<{id:string,name:string,color:string|null,description?:string,count:number}>>}
 */
export async function getLabels() {
  const data = await request("/api/v1/helpdesk/labels");
  return data.labels ?? [];
}

/** 409 = nombre duplicado o tope de 50 etiquetas. Solo admin/supervisor. */
export async function createLabel({ name, color, description }) {
  const data = await request(
    "/api/v1/helpdesk/labels",
    json("POST", {
      name,
      ...(color ? { color } : {}),
      ...(description ? { description } : {}),
    })
  );
  return data.label ?? data;
}

/** @param {{name?:string,color?:string,description?:string}} patch */
export async function updateLabel(id, patch) {
  const data = await request(`/api/v1/helpdesk/labels/${id}`, json("PATCH", patch));
  return data.label ?? data;
}

/** @returns {Promise<{deleted:boolean, unlabeled_count:number}>} */
export async function deleteLabel(id) {
  const data = await request(`/api/v1/helpdesk/labels/${id}`, { method: "DELETE" });
  return { deleted: data.deleted ?? true, unlabeled_count: data.unlabeled_count ?? 0 };
}

/**
 * Agrega/quita etiquetas de una conversación (ids del tenant, ≤10 cada lista).
 * @returns {Promise<object>} la conversación actualizada (con `labels`)
 */
export async function setConversationLabels(conversationId, { add = [], remove = [] }) {
  const data = await request(
    `/api/v1/conversations/${conversationId}/labels`,
    json("PATCH", { add, remove })
  );
  return data.conversation ?? data;
}

// ── Notas privadas ───────────────────────────────────────────────────────────

/**
 * Nota interna en el hilo: nunca llega al contacto ni a la IA.
 * @param {string} conversationId
 * @param {{content:string, mentions?:string[]}} payload  mentions = ids de usuario (≤10)
 * @returns {Promise<object>} el mensaje creado (sender_role 'system', metadata.kind 'private_note')
 */
export async function createPrivateNote(conversationId, { content, mentions = [] }) {
  const data = await request(
    `/api/v1/conversations/${conversationId}/notes`,
    json("POST", { content, ...(mentions.length ? { mentions } : {}) })
  );
  return data.message ?? data;
}

// ── Macros ───────────────────────────────────────────────────────────────────
// Acción: { type, params }. Tipos: add_labels {label_ids}, remove_labels
// {label_ids}, assign {agent_id|'self'}, set_folder {folder_id|null}, add_note
// {content}, send_message {content}, resolve {note?} (siempre la última).

/** Globales del tenant + personales del usuario. */
export async function getMacros() {
  const data = await request("/api/v1/helpdesk/macros");
  return data.macros ?? [];
}

/** @param {{name:string, visibility:'global'|'personal', actions:Array<{type:string,params?:object}>}} payload */
export async function createMacro(payload) {
  const data = await request("/api/v1/helpdesk/macros", json("POST", payload));
  return data.macro ?? data;
}

export async function updateMacro(id, patch) {
  const data = await request(`/api/v1/helpdesk/macros/${id}`, json("PATCH", patch));
  return data.macro ?? data;
}

export async function deleteMacro(id) {
  const data = await request(`/api/v1/helpdesk/macros/${id}`, { method: "DELETE" });
  return { deleted: data.deleted ?? true };
}

/**
 * Ejecuta la macro sobre la conversación, en orden; se detiene en la primera falla.
 * @returns {Promise<{ok:boolean, results:Array<{type:string, ok:boolean, error?:string}>, conversation?:object}>}
 */
export async function runMacro(conversationId, macroId) {
  return request(`/api/v1/conversations/${conversationId}/macros/${macroId}/run`, {
    method: "POST",
  });
}

// ── Reglas de automatización ─────────────────────────────────────────────────
// Regla: { name, enabled, event, conditions:[{field, operator, value}], actions }.
// event: conversation_created | message_received | conversation_reopened.
// field: channel (in) | account_key (in) | message_content (contains|not_contains)
//        | has_label | not_has_label | has_commerce (is) | business_hours (is)
//        | audience (in: driver|merchant|customer).
// Acciones permitidas: add_labels, remove_labels, assign, set_folder, add_note.

export async function getAutomationRules() {
  const data = await request("/api/v1/helpdesk/automation-rules");
  return data.rules ?? [];
}

export async function createAutomationRule(payload) {
  const data = await request("/api/v1/helpdesk/automation-rules", json("POST", payload));
  return data.rule ?? data;
}

export async function updateAutomationRule(id, patch) {
  const data = await request(`/api/v1/helpdesk/automation-rules/${id}`, json("PATCH", patch));
  return data.rule ?? data;
}

export async function deleteAutomationRule(id) {
  const data = await request(`/api/v1/helpdesk/automation-rules/${id}`, { method: "DELETE" });
  return { deleted: data.deleted ?? true };
}

// ── Copiloto IA ──────────────────────────────────────────────────────────────

/**
 * Resumen del caso o borrador de respuesta; NUNCA se envía solo.
 * 409 = IA apagada o sin agente para esta conversación; 429 = presupuesto diario agotado.
 * @param {string} conversationId
 * @param {'summary'|'reply'} mode
 * @returns {Promise<{text:string, agent_name:string|null}>}
 */
export async function askCopilot(conversationId, mode) {
  return request(`/api/v1/conversations/${conversationId}/copilot`, json("POST", { mode }));
}
